import { getBranding } from "@/branding";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  AuthorizedSellerCompanyListResponse,
  DeviceNote,
  AiAutofillResponse,
  AiDeviceMatchResponse,
  CancellationStatement,
  CancellationStatementDeviceLink,
  CancellationStatementListResponse,
  CompanyCancellationListResponse,
  EcolabelDevice,
  EcolabelDeviceCreateRequest,
  EcolabelDeviceFilters,
  EcolabelDeviceListResponse,
  EcolabelDeviceUpdateRequest,
  DuplicatePair,
  DuplicatePairListResponse,
  FilterOption,
  SubmissionBatchListResponse,
  SubmitDevicesRequest,
  SubmitDevicesResponse,
  UploadStatementResponse,
  IrecDeviceLookupResponse,
} from "./ecolabelsTypes";

export const ecolabelsApi = {
  // Device CRUD
  listDevices: (
    filters?: EcolabelDeviceFilters,
    offset?: number,
    limit?: number
  ) => {
    const params = new URLSearchParams();
    if (filters?.standard) params.append("standard", filters.standard);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.country) params.append("country", filters.country);
    if (filters?.renewable_type)
      params.append("renewable_type", filters.renewable_type);
    if (filters?.search) params.append("search", filters.search);
    if (offset !== undefined) params.append("offset", String(offset));
    if (limit !== undefined) params.append("limit", String(limit));

    const queryString = params.toString();
    const url = `/api/ecolabels/devices${queryString ? `?${queryString}` : ""}`;
    return goSolidApi.get<EcolabelDeviceListResponse>(url);
  },

  getDevice: (deviceId: number) =>
    goSolidApi.get<EcolabelDevice>(`/api/ecolabels/devices/${deviceId}`),

  createDevice: (data: EcolabelDeviceCreateRequest) =>
    goSolidApi.post<EcolabelDevice>("/api/ecolabels/devices", data),

  updateDevice: (deviceId: number, data: EcolabelDeviceUpdateRequest) =>
    goSolidApi.put<EcolabelDevice>(`/api/ecolabels/devices/${deviceId}`, data),

  deleteDevice: (deviceId: number) =>
    goSolidApi.delete(`/api/ecolabels/devices/${deviceId}`),

  bulkDeleteDevices: (deviceIds: number[]) =>
    goSolidApi.post<{ deleted_count: number }>(
      "/api/ecolabels/devices/bulk-delete",
      {
        device_ids: deviceIds,
      }
    ),

  // Device submission workflow (DEV-777). The chat endpoint itself is consumed by
  // AiChatCard via its `endpoint` prop: /api/ecolabels/devices/chat.
  submitDevices: (data: SubmitDevicesRequest) =>
    goSolidApi.post<SubmitDevicesResponse>(
      "/api/ecolabels/devices/submit",
      data
    ),

  listSubmissionBatches: (offset = 0, limit = 100) =>
    goSolidApi.get<SubmissionBatchListResponse>(
      `/api/ecolabels/devices/submissions?offset=${offset}&limit=${limit}`
    ),

  listSubmissionBatchDevices: (batchId: number) =>
    goSolidApi.get<{
      devices: EcolabelDevice[];
      pending_duplicate_counts: Record<number, number>;
    }>(`/api/ecolabels/devices/submissions/${batchId}/devices`),

  approveDevice: (deviceId: number, overrideDuplicates = false) =>
    goSolidApi.post<EcolabelDevice>(
      `/api/ecolabels/devices/${deviceId}/approve`,
      { override_duplicates: overrideDuplicates }
    ),

  rejectDevice: (deviceId: number, reason: string) =>
    goSolidApi.post<EcolabelDevice>(
      `/api/ecolabels/devices/${deviceId}/reject`,
      { reason }
    ),

  /** Internal EKOenergy review notes (admin-only; sellers get a 403). */
  listDeviceNotes: (deviceId: number) =>
    goSolidApi.get<{ notes: DeviceNote[] }>(
      `/api/ecolabels/devices/${deviceId}/notes`
    ),

  addDeviceNote: (deviceId: number, note: string) =>
    goSolidApi.post<DeviceNote>(`/api/ecolabels/devices/${deviceId}/notes`, {
      note,
    }),

  /** Public I-REC device-register data for one device code (read-only proxy; the
   * browser cannot call Evident directly). Never a 404: `status` says what happened. */
  lookupIrecDevice: (code: string) =>
    goSolidApi.get<IrecDeviceLookupResponse>(
      `/api/ecolabels/irec-devices/${encodeURIComponent(code)}`
    ),

  getDeviceDuplicates: (deviceId: number) =>
    goSolidApi.get<{ pairs: DuplicatePair[] }>(
      `/api/ecolabels/devices/${deviceId}/duplicates`
    ),

  // Duplicate cleaner (DEV-777)
  listDuplicatePairs: (status = "PENDING", offset = 0, limit = 100) =>
    goSolidApi.get<DuplicatePairListResponse>(
      `/api/ecolabels/duplicates?status=${status}&offset=${offset}&limit=${limit}`
    ),

  triggerDuplicateScan: () =>
    goSolidApi.post<{ task_id: string }>("/api/ecolabels/duplicates/scan", {}),

  resolveDuplicatePair: (
    pairId: number,
    resolution: "not_duplicate" | "merge",
    canonicalDeviceId?: number
  ) =>
    goSolidApi.post<DuplicatePair>(
      `/api/ecolabels/duplicates/${pairId}/resolve`,
      {
        resolution,
        ...(canonicalDeviceId !== undefined
          ? { canonical_device_id: canonicalDeviceId }
          : {}),
      }
    ),

  assessDuplicatePair: (pairId: number) =>
    goSolidApi.post<{ assessment: DuplicatePair["ai_assessment"] }>(
      `/api/ecolabels/duplicates/${pairId}/assess`,
      {}
    ),

  // Export
  exportDevices: (filters?: {
    standards?: string[];
    statuses?: string[];
    countries?: string[];
    renewable_types?: string[];
    search?: string;
    device_ids?: number[];
  }) =>
    goSolidApi.post("/api/ecolabels/devices/export", filters ?? {}, {
      responseType: "blob",
    }),

  // Filters
  getCountries: () =>
    goSolidApi.get<{ countries: string[] }>("/api/ecolabels/filters/countries"),

  getRenewableTypes: () =>
    goSolidApi.get<{ renewable_types: string[] }>(
      "/api/ecolabels/filters/renewable-types"
    ),

  getStandards: () =>
    goSolidApi.get<{ standards: FilterOption[] }>(
      "/api/ecolabels/filters/standards"
    ),

  getStatuses: () =>
    goSolidApi.get<{ statuses: FilterOption[] }>(
      "/api/ecolabels/filters/statuses"
    ),

  // Cancellation linking
  linkCancellation: (data: {
    cancellation_id: number;
    ecolabel_device_ids: number[];
    mwh_attributed?: Record<number, number>;
  }) => goSolidApi.post("/api/ecolabels/devices/link-cancellation", data),

  // Document endpoints
  getDeviceDocuments: (deviceId: number) =>
    goSolidApi.get<{
      documents: {
        id: number;
        company_id: number;
        file_name: string;
        upload_source: string;
        created_at: string;
      }[];
      count: number;
    }>(`/api/ecolabels/devices/${deviceId}/documents`),

  downloadDocument: (companyId: number, uploadId: number) =>
    goSolidApi.get(`/api/company/${companyId}/download/${uploadId}`, {
      responseType: "blob",
    }),

  getDocumentCounts: (deviceIds: number[]) =>
    goSolidApi.post<{ counts: Record<number, number> }>(
      "/api/ecolabels/devices/document-counts",
      { device_ids: deviceIds }
    ),

  // AI endpoints
  analyzeDocument: (companyId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", String(companyId));
    return goSolidApi.post<{
      status: string;
      data: {
        devices: EcolabelDeviceCreateRequest[];
        document_type: string;
        confidence: number;
        upload_ids: number[];
        conversation_id: number;
      };
    }>("/api/ecolabels/ai/analyze-document", formData);
  },

  matchDevices: (companyId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", String(companyId));
    return goSolidApi.post<{
      status: string;
      data: AiDeviceMatchResponse;
      upload_ids: number[];
      conversation_id: number;
      total_mwh: number;
    }>("/api/ecolabels/ai/match-devices", formData);
  },

  autofillDevice: (companyId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", String(companyId));
    return goSolidApi.post<{
      status: string;
      data: AiAutofillResponse;
      upload_ids: number[];
      conversation_id: number;
    }>("/api/ecolabels/ai/autofill", formData);
  },

  // --- Cancellation Statements ---

  listStatements: (offset?: number, limit?: number, companyId?: number) => {
    const params = new URLSearchParams();
    if (offset !== undefined) params.append("offset", String(offset));
    if (limit !== undefined) params.append("limit", String(limit));
    if (companyId !== undefined) params.append("company_id", String(companyId));
    const queryString = params.toString();
    const url = `/api/ecolabels/statements${queryString ? `?${queryString}` : ""}`;
    return goSolidApi.get<CancellationStatementListResponse>(url);
  },

  getStatement: (statementId: number) =>
    goSolidApi.get<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}`
    ),

  uploadStatement: (
    companyId: number,
    file?: File | null,
    cancellationId?: number
  ) => {
    const formData = new FormData();
    formData.append("company_id", String(companyId));
    if (file) {
      formData.append("file", file);
    }
    if (cancellationId) {
      formData.append("cancellation_id", String(cancellationId));
    }
    return goSolidApi.post<UploadStatementResponse>(
      "/api/ecolabels/statements/upload",
      formData
    );
  },

  /**
   * Create a cancellation statement from a document previously uploaded via AI chat.
   *
   * @param data - Statement creation payload including company_id, statement_upload_id (from chat), beneficiary details, period, purpose, total_mwh, optional notes, and device attributions
   * @returns CancellationStatement - The newly created statement record
   * @throws API error if statement_upload_id is invalid, total_mwh is fractional (backend expects int | None), or required fields are missing
   *
   * Route: POST /api/ecolabels/statements/create-from-chat
   */
  createStatementFromChat: (data: {
    company_id: number;
    statement_upload_id: number;
    beneficiary_name: string | null;
    registry_code: string | null;
    country: string | null;
    period_start: string | null;
    period_end: string | null;
    purpose: string | null;
    total_mwh: number | null;
    notes: string | null;
    device_links: CancellationStatementDeviceLink[];
  }) =>
    goSolidApi.post<CancellationStatement>(
      "/api/ecolabels/statements/create-from-chat",
      data
    ),

  listAvailableCancellations: (companyId?: number) => {
    const params = new URLSearchParams();
    if (companyId) params.append("company_id", String(companyId));
    const queryString = params.toString();
    const url = `/api/ecolabels/cancellations/available${queryString ? `?${queryString}` : ""}`;
    return goSolidApi.get<CompanyCancellationListResponse>(url);
  },

  updateStatementDevices: (
    statementId: number,
    deviceLinks: CancellationStatementDeviceLink[]
  ) =>
    goSolidApi.put<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}/devices`,
      { device_links: deviceLinks }
    ),

  updateStatement: (
    statementId: number,
    data: {
      company_id?: number | null;
      beneficiary_name: string | null;
      registry_code: string | null;
      country: string | null;
      period_start: string | null;
      period_end: string | null;
      purpose: string | null;
      total_mwh: number | null;
      notes: string | null;
      device_links?: CancellationStatementDeviceLink[];
    }
  ) =>
    goSolidApi.put<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}`,
      data
    ),

  replaceStatementDocument: (
    statementId: number,
    documentType: "statement" | "certificate",
    file: File
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    return goSolidApi.post<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}/documents/${documentType}`,
      formData
    );
  },

  deleteStatementDocument: (
    statementId: number,
    documentType: "statement" | "certificate"
  ) =>
    goSolidApi.delete<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}/documents/${documentType}`
    ),

  approveStatement: (statementId: number) =>
    goSolidApi.post<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}/approve`
    ),

  rejectStatement: (statementId: number) =>
    goSolidApi.post<CancellationStatement>(
      `/api/ecolabels/statements/${statementId}/reject`
    ),

  generateStatementCertificate: (statementId: number) =>
    goSolidApi.post(
      `/api/ecolabels/statements/${statementId}/generate-certificate`,
      {},
      { responseType: "blob" }
    ),

  listAuthorizedSellerCompanies: () =>
    goSolidApi.get<AuthorizedSellerCompanyListResponse>(
      "/api/ecolabels/companies/authorized-sellers"
    ),

  /**
   * POST /api/ecolabels/companies/authorized-sellers — EKOenergy-admin-only quick
   * create: makes the seller company (flagged ekoenergy_authorized_seller) and emails
   * the owner an invitation. `portal` is this UI's branding key; the invitation's join
   * link points back at that host. 403 for non-admins; 400 on validation failures
   * (e.g. blank name, invalid email).
   */
  createAuthorizedSellerCompany: (name: string, email: string) =>
    goSolidApi.post<{ id: number; name: string; invited_email: string }>(
      "/api/ecolabels/companies/authorized-sellers",
      { name, email, portal: getBranding().key }
    ),
};
